import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { LineStateEnum } from 'src/app/samwebDescription/samwin/LineStateEnum';
import { UserStatusEnum } from 'src/app/samwebDescription/samwin/UserStatusEnum';
import { SamwinAgentListState } from 'src/app/store/samwin/samwin-agent-list/samwin-agent-list.state';
import { ReloadCallHistoryForUser } from 'src/app/store/samwin/samwin-call-history/samwin-call-history.action';
import { SamwinCallHistoryState } from 'src/app/store/samwin/samwin-call-history/samwin-call-history.state';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { Skill } from 'src/app/store/samwin/samwin-skills/skill';
import { SkillConstants } from 'src/app/store/samwin/samwin-skills/skill-constants';
import { SkillGroup } from 'src/app/store/samwin/samwin-skills/skill-group';
import { SupervisingChangeSkillStatus } from 'src/app/store/samwin/supervising.actions';
import { SessionState } from 'src/app/store/session/session.state';
import { AgentListEntryRemoteDto } from 'src/generated/signalR/agent-list-entry-remote-dto';
import { SkillUserConfigurationRemoteDto } from 'src/generated/signalR/skill-user-configuration-remote-dto';
import { StatusType } from 'src/generated/signalR/status-type';
import { UserStatusInfoRemoteDto } from 'src/generated/signalR/user-status-info-remote-dto';
import { UsersService } from 'src/generated/swagger';
import { AgentDetailsSettingState } from '../../../brick-settings/specific-brick-settings/agent-details-brick-settings/agent-details-settings.state';
import { AgentListEntrySelected } from '../agent-list-behaviour/agent-list-behaviour.actions';
import { ChangeSkillStatusOfSelectedAgent, RefreshSelectedAgentData, ToggleAgentDetailsEditMode, UpdateAgentData } from './agent-details-behavior.actions';

export interface AgentDetailsBehaviourStateModel {
   userId: number;
   skillsOfUser: SkillUserConfigurationRemoteDto[];
   statusesOfUser: UserStatusInfoRemoteDto[];
   featuresOfUser: string[];
   isInEditMode: boolean;
}

@State<AgentDetailsBehaviourStateModel>({
   name: 'agentDetailsBehaviour',
   defaults: {
      userId: 0,
      skillsOfUser: [],
      statusesOfUser: [],
      featuresOfUser: [],
      isInEditMode: false,
   },
})
@Injectable()
export class AgentDetailsBehaviourState {
   private agentListEntrySelectedSubject = new Subject<AgentListEntrySelected>();

   constructor(private store: Store, private usersService: UsersService) {
      this.agentListEntrySelectedSubject.pipe(debounceTime(300)).subscribe((action) => {
         this.store.dispatch(new RefreshSelectedAgentData());
      });
   }

   @Selector([AgentDetailsBehaviourState])
   public static skillsOfSelectedAgent(state: AgentDetailsBehaviourStateModel) {
      return state.skillsOfUser;
   }

   @Selector([AgentDetailsBehaviourState.skillsOfSelectedAgent])
   public static skillIdsOfSelectedAgent(skills: SkillUserConfigurationRemoteDto[]) {
      return skills.map((skill) => skill.id);
   }

   @Selector([AgentDetailsBehaviourState])
   public static getCurrentSelectedAgentId(state: AgentDetailsBehaviourStateModel) {
      return state.userId;
   }

   @Selector([AgentDetailsBehaviourState])
   public static skillsOfUser(state: AgentDetailsBehaviourStateModel) {
      return state.skillsOfUser;
   }

   @Selector([AgentDetailsBehaviourState])
   public static statusesOfUser(state: AgentDetailsBehaviourStateModel) {
      return state.statusesOfUser;
   }

   @Selector([AgentDetailsBehaviourState])
   public static isInEditMode(state: AgentDetailsBehaviourStateModel) {
      return state.isInEditMode;
   }

   @Selector([AgentDetailsBehaviourState])
   public static featuresOfUser(state: AgentDetailsBehaviourStateModel) {
      return state.featuresOfUser;
   }

   @Selector([AgentDetailsBehaviourState.featuresOfUser])
   public static getEnabledFeaturesForSelectedAgentId(featuresOfUser: string[]): FeatureEnum[] {
      return featuresOfUser.map((x) => FeatureEnum.valueOf(x));
   }

   public static isFeatureEnabledForSelectedAgentId(feature: FeatureEnum): (features: FeatureEnum[]) => boolean {
      return createSelector([AgentDetailsBehaviourState.getEnabledFeaturesForSelectedAgentId], (enabledFeatures: FeatureEnum[]) => {
         return _.includes(enabledFeatures, feature);
      });
   }

   @Selector([AgentDetailsBehaviourState.skillsOfSelectedAgent, SamwinSkillsState.getSkillWithIdFn])
   public static getSkillGroupsForSelectedAgentId(skills: SkillUserConfigurationRemoteDto[], getSkillWithIdFn: (skillId: number) => Skill) {
      const skillConfigurationsforUser = _(skills).map((skillConfiguration) => {
         const skill = getSkillWithIdFn(skillConfiguration.id);
         skill.skillUserConfiguration = skillConfiguration;
         return skill;
      });

      const groupedSkillsforUser = skillConfigurationsforUser
         .groupBy((skill) => skill?.skillConfiguration?.skillName ?? SkillConstants.UNKNOWN_SKILL_NAME)
         .map((skill) => {
            return new SkillGroup(skill);
         })
         .orderBy((skillGroup) => skillGroup.skillConfiguration.skillName.toLowerCase() ?? SkillConstants.UNKNOWN_SKILL_NAME)
         .value();

      return groupedSkillsforUser;
   }

   @Selector([AgentDetailsBehaviourState.getCurrentSelectedAgentId, SamwinAgentListState.getAgentDetailsForUserId])
   public static getAgentDetailsForSelectedAgentId(
      selectedAgentId: number,
      getAgentDetailsForUserIdFn: (userId) => null | AgentListEntryRemoteDto
   ): null | AgentListEntryRemoteDto {
      return getAgentDetailsForUserIdFn(selectedAgentId);
   }

   @Selector([AgentDetailsBehaviourState.getAgentDetailsForSelectedAgentId])
   public static getSelectedAgentUserStatus(selectedAgent: null | AgentListEntryRemoteDto): UserStatusInfoRemoteDto {
      return selectedAgent?.userStatus;
   }

   @Selector([AgentDetailsBehaviourState.getSelectedAgentUserStatus])
   public static getSelectedAgentIsOnline(userStatus: UserStatusInfoRemoteDto): boolean {
      if (_.isNil(userStatus)) {
         return false;
      }
      return userStatus?.id !== UserStatusEnum.OFFLINE.value;
   }

   @Selector([AgentDetailsBehaviourState.getSelectedAgentUserStatus])
   public static getSelectedAgentIsLoggedOn(userStatus: UserStatusInfoRemoteDto): boolean {
      if (_.isNil(userStatus)) {
         return false;
      }
      return userStatus?.type !== StatusType.LOGGED_OFF;
   }

   @Selector([
      SamwinCallHistoryState.getCallListForUser,
      AgentDetailsSettingState.getNumOfCallHistoryEntriesToShow,
      AgentDetailsBehaviourState.getCurrentSelectedAgentId,
      SessionState.getAuthenticatedUserId,
   ])
   public static getCallHistoryForSelectedAgent(callListForUser, numOfCallHistoryEntriesToShow, agentId, authenticatedUserId) {
      return (brickInstanceId) => {
         const numOfCallHistoryEntries = numOfCallHistoryEntriesToShow(brickInstanceId);
         const list = callListForUser(agentId);
         return _(list).take(numOfCallHistoryEntries).value();
      };
   }

   @Selector([
      AgentDetailsBehaviourState.statusesOfUser,
      AgentDetailsBehaviourState.getCurrentSelectedAgentId,
      SamwinAgentListState.getAgentDetailsForUserId,
      AgentDetailsBehaviourState.isFeatureEnabledForSelectedAgentId(FeatureEnum.CCO_PERM_USE_PAUSE),
      AgentDetailsBehaviourState.isFeatureEnabledForSelectedAgentId(FeatureEnum.CCO_PERM_MAY_SET_ACW),
   ])
   public static getAllFollowUpStatusInfosForSelectedUser(
      statusesOfUser: UserStatusInfoRemoteDto[],
      userId: number,
      agentDetailsForUserIdFn: (userId: number) => null | AgentListEntryRemoteDto,
      mayUsePause: boolean,
      mayUseAcwEnabled: boolean
   ): UserStatusInfoRemoteDto[] {
      // remove the busy and call waiting statuses
      let followUpStates = statusesOfUser.filter((info) => {
         return info.id !== UserStatusEnum.BUSY.value && info.id !== UserStatusEnum.CALL_WAITING.value;
      });

      const agentDetails = agentDetailsForUserIdFn(userId);

      // while being BUSY or LOGGED_OFF/BUSY (logged-off but in a call)
      // it is only allowed to switch to other logged-off states
      if (!_.isNil(agentDetails?.userStatus)) {
         if (
            agentDetails.userStatus.id === UserStatusEnum.BUSY.value ||
            (agentDetails.userStatus.type === StatusType.LOGGED_OFF && LineStateEnum.fromEnumValue(agentDetails.callState).isCallInProgress)
         ) {
            followUpStates = followUpStates.filter((status) => status.type === StatusType.LOGGED_OFF);
         }
      }

      if (!mayUsePause) {
         followUpStates = followUpStates.filter((status) => status.id !== UserStatusEnum.DND.value);
      }

      if (!mayUseAcwEnabled) {
         followUpStates = followUpStates.filter((status) => status.id !== UserStatusEnum.ACW.value);
      }

      return followUpStates;
   }

   @Action(ToggleAgentDetailsEditMode)
   public toggleAgentDetailsEditMode(ctx: StateContext<AgentDetailsBehaviourStateModel>, action: ToggleAgentDetailsEditMode) {
      ctx.patchState({
         isInEditMode: !_.cloneDeep(ctx.getState()).isInEditMode,
      });
   }

   @Action(UpdateAgentData)
   public updateAgentData(ctx: StateContext<AgentDetailsBehaviourStateModel>, action: UpdateAgentData) {
      ctx.patchState({
         userId: action.userId,
         skillsOfUser: action.skillsOfUser,
         statusesOfUser: action.statusesOfUser,
         featuresOfUser: action.featuresOfUser,
      });
   }

   @Action(AgentListEntrySelected)
   public agentListEntrySelected(ctx: StateContext<AgentDetailsBehaviourStateModel>, action: AgentListEntrySelected) {
      if (action.userId === ctx.getState().userId) {
         return;
      }
      ctx.dispatch(new UpdateAgentData(action.userId, [], [], []));

      if (_.isNull(action.userId)) {
         return;
      }

      this.agentListEntrySelectedSubject.next(action);
   }

   @Action(RefreshSelectedAgentData)
   public refreshSelectedAgentData(ctx: StateContext<AgentDetailsBehaviourStateModel>, action: RefreshSelectedAgentData) {
      const userId = ctx.getState().userId;
      const maxNumOfEntriesInCallHistory = this.store.selectSnapshot(AgentDetailsSettingState.getMaxNumOfEntriesInCallHistory);
      ctx.dispatch(new ReloadCallHistoryForUser(userId, maxNumOfEntriesInCallHistory));

      return combineLatest([
         this.usersService.getSkillsForUser(userId),
         this.usersService.getStatusesForUser(userId),
         this.usersService.getFeaturesForUser(userId),
      ]).pipe(
         map(([getSkillsForUserResponse, getStatusesForUserResponse, getFeaturesForUserResponse]) => {
            return ctx.dispatch(
               new UpdateAgentData(
                  userId,
                  getSkillsForUserResponse.skills as SkillUserConfigurationRemoteDto[],
                  getStatusesForUserResponse.statuses as UserStatusInfoRemoteDto[],
                  getFeaturesForUserResponse.features
               )
            );
         })
      );
   }

   @Action(ChangeSkillStatusOfSelectedAgent)
   public changeSkillStatusOfSelectedAgent(ctx: StateContext<AgentDetailsBehaviourStateModel>, action: ChangeSkillStatusOfSelectedAgent) {
      this.store.dispatch(new SupervisingChangeSkillStatus(ctx.getState().userId, action.skillId, action.shouldSkillBeActive));
   }
}
